/**
 * Service Worker registration with accurate, non-disruptive updates.
 *
 * Two rules drive everything here:
 *  1. A fresh load (cold reopen, refresh, hard refresh) should always end up on
 *     the latest deployed build — so any waiting worker is activated when the
 *     user hasn't started interacting yet.
 *  2. A mid-session update must NEVER yank the page out from under someone who
 *     is typing — so once the user has interacted we defer to a version-gated
 *     "Update available" toast instead of auto-reloading.
 *
 * Accuracy: a reload only happens when the running build actually differs from
 * the deployed build (see app-version.ts). After a hard refresh that already
 * loaded the latest code, the still-`waiting` worker is the SAME version, so we
 * neither reload nor nag.
 */

import { isCanvas, isBusiness } from "@/mobile/appMode";
import { isUpdateReady } from "@/lib/app-version";

type SWUpdateDetail = { registration: ServiceWorkerRegistration };

/**
 * The "Update available" / refresh prompt belongs ONLY on internal surfaces:
 * the Canvas and Business native apps, and the logged-in CRM dashboard. It must
 * NEVER appear on customer-facing web — marketing pages, a business storefront,
 * or public form / share links.
 *
 * Returns true when the prompt should be SUPPRESSED for the given path.
 */
export function isBlockedPathForUpdatePrompt(pathname?: string): boolean {
    if (typeof window === "undefined") return false;
    if (isCanvas() || isBusiness()) return false;
    const p = pathname ?? window.location.pathname;
    const onDashboard = p.startsWith("/business/dashboard") || p.startsWith("/dashboard");
    return !onDashboard;
}

// Track whether the user has touched the page since this module loaded. If they
// have, we must NOT auto-reload — they're mid-form / mid-flow and a forced
// reload wipes uncontrolled UI state. The toast UX is the safe path.
let userHasInteracted = false;

// Set when the user explicitly opts in via the "Refresh" toast button. Bypasses
// the userHasInteracted guard in controllerchange (the tap itself flips
// userHasInteracted=true, which would otherwise skip the reload).
let userOptedInReload = false;
if (typeof window !== "undefined") {
    const mark = () => { userHasInteracted = true; };
    window.addEventListener("keydown", mark, { capture: true, passive: true });
    window.addEventListener("pointerdown", mark, { capture: true, passive: true });
    window.addEventListener("touchstart", mark, { capture: true, passive: true });
}

function activateWaiting(registration: ServiceWorkerRegistration): void {
    registration.waiting?.postMessage({ type: "SKIP_WAITING" });
}

// How long after page load a controllerchange still counts as "the initial
// load settling" (safe to silently reload) vs a mid-session update (notify only).
const INITIAL_LOAD_WINDOW_MS = 12_000;
// Background poll cadence: how often a live session re-checks for a new deploy.
const UPDATE_POLL_MS = 15 * 60 * 1000;

export function registerServiceWorker(): void {
    if (!("serviceWorker" in navigator)) return;

    window.addEventListener("load", async () => {
        const pageLoadAt = Date.now();
        try {
            const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
            console.log("[SW Register] registered:", registration.scope);

            // Transition safety: a worker left in `waiting` by an OLDER sw.js that
            // didn't skip-waiting still needs a nudge. (New deploys skip-waiting on
            // install, so this is only for the upgrade hop.)
            if (registration.waiting && !userHasInteracted) activateWaiting(registration);

            // ── Background update checks ─────────────────────────────────────
            // reg.update() re-fetches /sw.js (served no-cache); a newer build
            // skip-waits itself → the activated-worker handler below fires. Runs
            // now, every 15 min while the session is live, AND whenever the tab /
            // PWA regains focus — so a long-lived browser session or a reopened
            // PWA picks up a new deploy with NO user action.
            const checkForUpdate = () => { registration.update().catch(() => { /* offline */ }); };
            checkForUpdate();
            window.setInterval(checkForUpdate, UPDATE_POLL_MS);
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") checkForUpdate();
            });

            // A newer build's worker reached "activated" (it skip-waits on install
            // but does NOT claim, so THIS page keeps its current code — no broken
            // lazy chunks). We just decide what to do with the now-active worker.
            let refreshing = false;
            const watchWorker = (worker: ServiceWorker | null) => {
                if (!worker) return;
                worker.addEventListener("statechange", async () => {
                    if (worker.state !== "activated" || refreshing) return;
                    // First-ever install (no prior controller) → nothing to upgrade from.
                    if (!navigator.serviceWorker.controller) return;
                    const isInitialLoad = Date.now() - pageLoadAt < INITIAL_LOAD_WINDOW_MS;
                    if (userOptedInReload || (isInitialLoad && !userHasInteracted)) {
                        // Fresh cold open (or the user tapped Refresh) → reload onto the
                        // now-active worker, zero clicks. Version-gated so an already-
                        // current load never reloads for nothing.
                        if (userOptedInReload || (await isUpdateReady())) {
                            refreshing = true;
                            window.location.reload();
                        }
                        return;
                    }
                    // Mid-session (incl. the 15-min background poll) → NEVER yank.
                    // Notify with the version-gated toast; the update also lands on
                    // their next reopen/refresh on its own.
                    emitUpdateAvailable(registration);
                });
            };
            registration.addEventListener("updatefound", () => watchWorker(registration.installing));
            // A worker already mid-install when this handler attaches.
            if (registration.installing) watchWorker(registration.installing);
        } catch (error) {
            console.error("[SW Register] registration failed:", error);
        }
    });
}

/**
 * Activate the waiting SW and reload — the path the toast's "Refresh" button
 * takes. Sets userOptedInReload so controllerchange reloads even though the tap
 * flipped userHasInteracted=true. Falls back to a straight reload if no SW is
 * waiting, and to a timeout reload if controllerchange never fires.
 */
export function activateWaitingAndReload(registration: ServiceWorkerRegistration): void {
    userOptedInReload = true;
    if (!registration.waiting) {
        window.location.reload();
        return;
    }
    activateWaiting(registration);
    setTimeout(() => { window.location.reload(); }, 2000);
}

function emitUpdateAvailable(registration: ServiceWorkerRegistration): void {
    if ((window as Window & { __swManualCheck?: boolean }).__swManualCheck) return;
    if (isBlockedPathForUpdatePrompt()) {
        console.log("[SW Register] update detected on a gated path — deferring toast");
        return;
    }
    window.dispatchEvent(
        new CustomEvent<SWUpdateDetail>("sw-update-available", { detail: { registration } }),
    );
}
